import React, { useRef, useState, useEffect } from "react";
import {
  highlightBracketedText,
  highlightUnderscoreText,
} from "src/utils/utils";
import {
  ImageAndTextFieldsFragment,
} from "src/generated/graphql";
import { Typography } from "src/components/shared";
import { DynamicLink } from "src/components/shared"; 
import { FaArrowRightLong } from "react-icons/fa6";

type AutomaticContentProps = {
  content: ImageAndTextFieldsFragment;
  className?: string;
  batch?: boolean; 
};

const SiteScoreHeader = ({ content, className, batch }: AutomaticContentProps) => {
  const { eyebrowText, title, imageDescriptionJson, backgroundImage, buttonLink, image, videofile } = content;
  // State management
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState(750);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const itemDescription =
    imageDescriptionJson?.json?.content?.[0]?.content?.[0]?.value ?? '';

  // Check if we have both image and video
  const hasVideo = !!videofile?.url;
  const thumbnailUrl = image?.url ?? "";
  const videoUrl = videofile?.url ?? "";

  // Dynamic height calculation
  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const baseHeight = 750;
        const titleThreshold = 250;
        const extraContentHeight = Math.max(0, contentHeight - titleThreshold);
        setMinHeight(baseHeight + extraContentHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [title]);

  // Auto-play video if batch is true
  useEffect(() => {
    if (batch && hasVideo) {
      setShowVideo(true);
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch((error) => {
            console.log('Autoplay was prevented:', error);
            // Autoplay failed, but video is still shown and ready to play manually
          });
          setIsPlaying(true);
        }
      }, 10);
    }
  }, [batch, hasVideo]);

  // Video toggle functionality
  const togglePlayPause = () => {
    if (!showVideo) {
      setShowVideo(true);
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          setIsPlaying(true);
        }
      }, 10);
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <>
      <div className={`blog-banner sitecore-banner ${className}`} style={{ position: "relative" }}>
        <div
          className="blog-banner-block"
          style={{ minHeight: `${minHeight}px` }}
        >
          <img src={backgroundImage?.url ?? ""} alt="background" className="background-image" />

          <div className="blog-gradient-overlay"></div>
          <div className="container-fluid custom-spacer-col position-relative gap-5">
            <div
              ref={contentRef}
              className="blog-content-wrapper"
              style={{ marginTop: "170px" }}
            >
              <p className="heading-s" style={{ color: "#CDCDF3", letterSpacing: "0.3rem" }}>
                {eyebrowText}
              </p>
              
              <h1 className="blog-title blog-title-custom blog-heading-1 title123" 
                  dangerouslySetInnerHTML={{
                    __html: batch?  highlightUnderscoreText(title ?? "") : highlightBracketedText(title ?? "" , "bold"),
                  }}
              />
              
              {/* Replace author section with description and button */}
              <div className="blog-detail-header-user">
                <div className="blog-detail-header-user-details">
                  {itemDescription && (
                    <Typography
                      tag="p"
                      className="hero-description text-start light slide-up"
                    >
                      {itemDescription}
                    </Typography>
                  )}

                  {buttonLink && (
                    <DynamicLink
                      url={buttonLink.buttonLink ?? ""}
                      className="btn-white"
                    >
                      <span>{buttonLink.buttonText ?? ""}</span>
                      <FaArrowRightLong className="btn-icon" />
                    </DynamicLink>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Media display section - video or image based on videofile availability */}
        <div className="blog-detail-image-wrapper">
          {hasVideo ? (
            <>
              {!showVideo ? (
                <>
                  <img
                    src={thumbnailUrl}
                    alt="video thumbnail"
                    className="blog-detail-image"
                  />
                  {!batch && (
                    <button
                      onClick={togglePlayPause}
                      className="video-detail-play-pause-btn"
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M3 1L10 6L3 11V1Z" fill="black" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="blog-detail-image"
                    autoPlay={batch}
                    muted={batch}
                    loop={batch}
                    playsInline
                    style={{
                      objectFit: "contain",
                      objectPosition: "center",
                      backgroundColor: "black",
                    }}
                  />
                  {!batch && (
                    <button
                      onClick={togglePlayPause}
                      className="video-detail-play-pause-btn"
                    >
                      {isPlaying ? (
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 1V11"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M9 1V11"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M3 1L10 6L3 11V1Z" fill="black" />
                        </svg>
                      )}
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            <img
              src={thumbnailUrl}
              alt="content image"
              className="blog-detail-image"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SiteScoreHeader;